import { useCallback, useEffect, useMemo, useState } from "react"
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import useConnect, {
  type TAdsetAuditRow,
  type TCategory,
  computeMissingExclusions,
} from "./connect"
import ApplyExclusionsModal from "./ApplyExclusionsModal"
import { useApplyExclusionsMutation } from "~/ui-rtk/api/paidMediaApi"
import Breadcrumbs from "~/ui-rtk/components/layout/Breadcrumbs"
import { DateRangePicker, Button } from "~/ui-rtk/components/ui/controls"
import Select from "~/ui-rtk/components/ui/controls/Select"
import { Chip } from "~/ui-rtk/components/ui/common"
import { formatNumber } from "~/ui-rtk/utils/format"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import useDate from "~/ui-rtk/hooks/date"
import { DATE_FORMAT, isValidDate } from "~/ui-rtk/hooks/period"
import { brandRoutes } from "~/ui-rtk/constants/routes"
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline"

const CATEGORY_OPTIONS = [
  { label: "Needs Review", value: "needs_review" },
  { label: "Cold Prospecting", value: "cold" },
  { label: "Warm Prospecting", value: "warm" },
  { label: "Retention", value: "retention" },
]

const columnHelper = createColumnHelper<TAdsetAuditRow>()

export default function ExclusionsAuditPage() {
  const {
    hasDefinedAudiences,
    isLoading,
    adsetRows,
    engagedAudiences,
    existingAudiences,
    period,
    setPeriod,
  } = useConnect()
  const navigate = useNavigate()
  const dayjs = useDate()

  const [categoryOverrides, setCategoryOverrides] = useState<
    Record<string, TCategory>
  >({})
  const [modalMode, setModalMode] = useState<"single" | "all" | null>(null)
  const [selectedAdsetId, setSelectedAdsetId] = useState<string | null>(null)
  const [appliedExclusions, setAppliedExclusions] = useState<
    Record<string, string[]>
  >({})

  const [applyExclusions, { isLoading: isApplying }] =
    useApplyExclusionsMutation()

  useEffect(() => {
    document.title = "Marathon Data: Audits > Exclusions Audit"
  }, [])

  const handleCategoryChange = (adsetId: string, category: TCategory) => {
    setCategoryOverrides(prev => ({ ...prev, [adsetId]: category }))
  }

  const effectiveRows = useMemo(
    () =>
      adsetRows.map(row => {
        const overriddenCategory = categoryOverrides[row.adsetId]
        if (!overriddenCategory || overriddenCategory === row.category) {
          return row
        }
        const excludedIds = new Set(row.excludedAudiences.map(a => a.id))
        return {
          ...row,
          category: overriddenCategory,
          missingExclusions: computeMissingExclusions(
            overriddenCategory,
            excludedIds,
            engagedAudiences,
            existingAudiences,
          ),
        }
      }),
    [adsetRows, categoryOverrides, engagedAudiences, existingAudiences],
  )

  // Apply optimistic overlay — move applied audiences from missing to excluded
  const displayRows = useMemo(
    () =>
      effectiveRows.map(row => {
        const applied = appliedExclusions[row.adsetId]
        if (!applied || applied.length === 0) return row
        const appliedSet = new Set(applied)
        return {
          ...row,
          excludedAudiences: [
            ...row.excludedAudiences,
            ...row.missingExclusions.filter(a => appliedSet.has(a.id)),
          ],
          missingExclusions: row.missingExclusions.filter(
            a => !appliedSet.has(a.id),
          ),
        }
      }),
    [effectiveRows, appliedExclusions],
  )

  // Rows eligible for apply (has missing exclusions + not needs_review)
  const applicableRows = useMemo(
    () =>
      displayRows.filter(
        r => r.missingExclusions.length > 0 && r.category !== "needs_review",
      ),
    [displayRows],
  )

  const rowsForModal = useMemo(() => {
    if (modalMode === "single" && selectedAdsetId) {
      const row = displayRows.find(r => r.adsetId === selectedAdsetId)
      return row ? [row] : []
    }
    if (modalMode === "all") {
      return applicableRows
    }
    return []
  }, [modalMode, selectedAdsetId, displayRows, applicableRows])

  const handleOpenSingle = useCallback((adsetId: string) => {
    setSelectedAdsetId(adsetId)
    setModalMode("single")
  }, [])

  const handleOpenAll = useCallback(() => {
    setModalMode("all")
  }, [])

  const handleCloseModal = useCallback(() => {
    setModalMode(null)
    setSelectedAdsetId(null)
  }, [])

  const handleConfirm = useCallback(async () => {
    const payload = rowsForModal.map(r => ({
      adsetId: r.adsetId,
      audienceIds: r.missingExclusions.map(a => a.id),
    }))

    try {
      const result = await applyExclusions({
        platform: "meta",
        data: { adsets: payload },
      }).unwrap()

      // Optimistic update for successful items
      const newApplied: Record<string, string[]> = {}
      for (const item of result.results) {
        if (item.success) {
          const row = rowsForModal.find(r => r.adsetId === item.adsetId)
          if (row) {
            newApplied[item.adsetId] = [
              ...(appliedExclusions[item.adsetId] || []),
              ...row.missingExclusions.map(a => a.id),
            ]
          }
        }
      }
      setAppliedExclusions(prev => ({ ...prev, ...newApplied }))

      if (result.summary.failed === 0) {
        toast.success(
          `Applied exclusions to ${result.summary.successful} adset${result.summary.successful !== 1 ? "s" : ""}`,
        )
      } else {
        toast.warn(
          `${result.summary.successful} succeeded, ${result.summary.failed} failed`,
        )
      }
    } catch {
      toast.error("Failed to apply exclusions")
    }

    handleCloseModal()
  }, [rowsForModal, applyExclusions, appliedExclusions, handleCloseModal])

  const columns = useMemo(
    () => [
      columnHelper.accessor("category", {
        header: "Category",
        size: 180,
        cell: ({ row }) => (
          <div>
            <Select
              options={CATEGORY_OPTIONS}
              value={row.original.category}
              onChange={e =>
                handleCategoryChange(
                  row.original.adsetId,
                  e.target.value as TCategory,
                )
              }
              classes={{ select: "min-w-[160px] h-10 text-sm" }}
            />
            {row.original.category === "needs_review" && (
              <div className="flex items-center gap-1 mt-1 text-xs text-yellow-400">
                <ExclamationTriangleIcon className="w-3 h-3" />
                <span>Select a category</span>
              </div>
            )}
          </div>
        ),
      }),
      columnHelper.accessor("adsetName", {
        header: "Adset Name",
        size: 250,
        cell: ({ row }) => (
          <div>
            <div className="font-medium text-sm">{row.original.adsetName}</div>
            <div className="text-xs text-cadet-blue mt-0.5">
              {row.original.campaignName}
            </div>
          </div>
        ),
      }),
      columnHelper.accessor("spend", {
        header: "Spend",
        size: 100,
        cell: ({ getValue }) =>
          formatNumber(getValue(), { currency: true, compact: true }),
      }),
      columnHelper.accessor("excludedAudiences", {
        header: "Excluded Audiences",
        size: 300,
        cell: ({ getValue }) => {
          const audiences = getValue()
          if (audiences.length === 0) {
            return <span className="text-xs text-cadet-blue">None</span>
          }
          return (
            <div className="flex flex-wrap gap-1">
              {audiences.map(a => (
                <Chip key={a.id} className="bg-basic-blue text-xs">
                  {a.name}
                </Chip>
              ))}
            </div>
          )
        },
      }),
      columnHelper.accessor("missingExclusions", {
        header: "Missing Exclusions",
        size: 300,
        cell: ({ row }) => {
          const missing = row.original.missingExclusions
          if (row.original.category === "needs_review") {
            return (
              <span className="text-xs text-cadet-blue">
                Categorize this adset to see missing exclusions
              </span>
            )
          }
          if (missing.length === 0) {
            return (
              <span className="inline-flex items-center gap-1 text-xs text-green-400 font-medium">
                <span className="inline-block w-2 h-2 rounded-full bg-green-400" />
                All Good
              </span>
            )
          }
          return (
            <div className="flex flex-wrap gap-1">
              {missing.map(a => (
                <Chip key={a.id} className="bg-red-600/20 text-red-400 text-xs">
                  {a.name}
                </Chip>
              ))}
            </div>
          )
        },
      }),
      columnHelper.display({
        id: "actions",
        header: "",
        size: 160,
        cell: ({ row }) => {
          if (
            row.original.category === "needs_review" ||
            row.original.missingExclusions.length === 0
          ) {
            return null
          }
          return (
            <Button
              variant={{ variant: "outlined" }}
              size="sm"
              onClick={() => handleOpenSingle(row.original.adsetId)}
            >
              Apply Exclusions
            </Button>
          )
        },
      }),
    ],
    [],
  )

  const table = useReactTable({
    data: displayRows,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  // DateRangePicker date conversions
  const dateRangeStart = useMemo(
    () =>
      period[0] && isValidDate(period[0])
        ? dayjs(period[0]).toDate()
        : new Date(),
    [period[0]],
  )
  const dateRangeEnd = useMemo(
    () =>
      period[1] && isValidDate(period[1])
        ? dayjs(period[1]).toDate()
        : new Date(),
    [period[1]],
  )

  const handleChangePeriods = ({ start, end }: { start: Date; end: Date }) => {
    setPeriod(dayjs(start).format(DATE_FORMAT), dayjs(end).format(DATE_FORMAT))
  }

  return (
    <div>
      {/* Header */}
      {/* Note: no MetaAdAccountGate here — the page's data load is company-
          scoped through Cube.js (filtered server-side by the user's JWT) and
          the apply-exclusions mutation operates per-adset, so a "select ad
          account" gate would only capture dead state and mislead users into
          thinking the table is scoped to their pick. The mutation already
          uses the company's saved ad account via backend resolution. */}
      <div className="text-start grid grid-cols-2 items-center">
        <Breadcrumbs
          items={[
            { slug: "/audits", name: "Audits" },
            { name: "Exclusions Audit" },
          ]}
        />
        <div className="flex justify-end items-center gap-3">
          {applicableRows.length > 0 && (
            <Button
              variant={{ variant: "solid", color: "pink" }}
              size="sm"
              onClick={handleOpenAll}
            >
              Apply All ({applicableRows.length})
            </Button>
          )}
          <DateRangePicker
            start={dateRangeStart}
            end={dateRangeEnd}
            defaultPreset="last30Days"
            onChange={handleChangePeriods}
          />
        </div>
      </div>

      <div className="mt-6">
        {/* Warning: no defined audiences */}
        {!isLoading && !hasDefinedAudiences && (
          <div className="flex items-start gap-3 bg-yellow-600/10 border border-yellow-600/30 rounded-lg p-4 mb-4">
            <ExclamationTriangleIcon className="w-5 h-5 text-yellow-400 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-medium text-yellow-400">
                Audience classifications not configured
              </p>
              <p className="text-xs text-cadet-blue mt-1">
                Classify your Engaged and Existing Customer audiences to
                auto-detect adset categories and identify missing exclusions.
              </p>
              <Button
                variant={{ variant: "solid", color: "pink" }}
                size="sm"
                className="mt-2"
                onClick={() => navigate(brandRoutes.SETUP.AUDIENCES)}
              >
                Set Up Audiences
              </Button>
            </div>
          </div>
        )}

        {/* Loading */}
        {isLoading && (
          <div className="flex items-center justify-center py-16">
            <span className="loading loading-spinner loading-lg" />
          </div>
        )}

        {/* Table */}
        {!isLoading && (
          <div className="overflow-x-auto">
            <table className="table w-full">
              <thead>
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <th
                        key={header.id}
                        className="text-xs text-cadet-blue font-medium uppercase tracking-wider"
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={columns.length}
                      className="text-center py-8 text-cadet-blue"
                    >
                      No adsets with spend found in the selected period.
                    </td>
                  </tr>
                ) : (
                  table.getRowModel().rows.map(row => (
                    <tr
                      key={row.id}
                      className={cn(
                        "border-b border-basic-blue/30",
                        row.original.missingExclusions.length > 0 &&
                          "bg-red-600/5",
                        row.original.category === "needs_review" &&
                          "bg-yellow-600/5",
                      )}
                    >
                      {row.getVisibleCells().map(cell => (
                        <td key={cell.id} className="py-3 align-top">
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ApplyExclusionsModal
        isOpen={modalMode !== null}
        onClose={handleCloseModal}
        onConfirm={handleConfirm}
        isLoading={isApplying}
        rows={rowsForModal}
      />
    </div>
  )
}
